import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Field, FormSection, getFormValues } from "redux-form";

import { ReduxControls, ErrorBanner } from "rhombus-react";

import DynamicAssetSelect from "social/DynamicAssetSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { isRequiredAssetField, prettifyMessages } from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { PINTEREST_IMAGE_CONSTRAINTS } from "social/validators/pinterest";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import PinterestDisclosure from "social/PinterestDisclosure";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";


const {
	MAX_LENGTH_TITLE,
	MAX_LENGTH_DESCRIPTION,
	MAX_LENGTH_ALT_TEXT,
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_BYTES,
	IMAGE_ACCEPT,
} = PINTEREST_IMAGE_CONSTRAINTS;

export const PINTEREST_IMAGE_FIELDS = {
	isDynamic,
	assetType,
	imposiumProject,
	imposiumComposition: imposiumCompositionWithoutProjectName,
	image: {
		name: "image",
		label: "Image",
		tip: {
			description: `Pinterest recommends using a 2:3 aspect ratio, or 1000 x 1500 pixels.
			Pins with an aspect ratio greater than 2:3 might get cut off in people's feeds`,
			direction: "top",
		},
		assetType: ASSET_TYPES.IMAGE,
		accept: IMAGE_ACCEPT,
		component: DynamicAssetSelect,
		defaultDynVarName: "product",
		required: true,
		className: "full-width",
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum`,
			},
			{
				name: "File Types",
				description: IMAGE_ACCEPT,
			},
			{
				name: "Dimensions",
				description: "2:3 aspect ratio or 1000 x 1500 pixels",
			}
		],
	},
	name: {
		name: "name",
		label: "Creative Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
	},
	title: {
		name: "title",
		label: "Title",
		placeholder: "Enter a short headline", // Should this be title?
		component: DynamicInput,
		defaultDynVarName: "headline",
		className: "full-width",
		tip: {
			direction: "right",
			description: "May get truncated after 40 characters",
		},
		charMax: MAX_LENGTH_TITLE,
		// showCharCount: true,
	},
	description: {
		name: "description",
		label: "Description",
		placeholder: "Tell people what your creative is about",
		component: DynamicInput,
		defaultDynVarName: "description",
		className: "full-width",
		tip: {
			direction: "right",
			description: `Descriptions are used by the Pinterest algorithm to determine relevance for delivery,
			do not appear when viewing the Pin in the home or search feed, and do not appear for ads when viewed up close.`,
			size: 'xlarge',
		},
		charMax: MAX_LENGTH_DESCRIPTION,
		// showCharCount: true,
	},
	altText: {
		name: "altText",
		label: "Alt Text",
		placeholder: "Text for screen readers...",
		component: DynamicInput,
		defaultDynVarName: "alt_text",
		charMax: MAX_LENGTH_ALT_TEXT,
		className: "full-width",
		tip: {
			direction: "right",
			description: `Explain what people can see in the pin. This text will be read aloud by screen readers.`,
			size: 'xlarge',
		},
	},
	disclosure: {
		name: "disclosureEnabled",
		component: PinterestDisclosure,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
	},
};

const adType = SOCIAL_AD_TYPES.PINTEREST_IMAGE;

export default function PinterestImage(props) {
	const { navigationActions, libraryId, isViewOnly, showPristineErrors, doVersionsExist } = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[adType] || {};

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, PINTEREST_IMAGE_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(PINTEREST_IMAGE_FIELDS).map((fieldProps) => {
		const extraProps = {
		};

		if (fieldProps.name === "image") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
			extraProps.required = isRequiredAssetField(assetType);
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return (
			<Field
				key={fieldProps.name}
				{...fieldProps}
				{...extraProps}
			/>
		);
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

PinterestImage.displayName = "PinterestImageForm";
